import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

import Form from "ui/Form";
import Stepper from "ui/Stepper";

import DialogActions, { DialogActionsPropTypes } from "./DialogActions";
import DialogContent, { DialogContentPropTypes } from "./DialogContent";
import DialogHeader, { DialogHeaderPropTypes } from "./DialogHeader";
import Dialog, { DialogPropTypes } from "./Dialog";

import useDialogFormik from "hooks/useDialogFormik";

function DialogForm(props) {
  const {
    children,
    steps,
    activeStep,
    onClose,
    isLoading,
    Icon,
    preLoading,
    initialValues,
    onSubmit,
    enableReinitialize,
    validationSchema,
  } = props;
  const hasChildren = !!children;
  const hasSteps = Boolean(steps && !!steps.length);
  const formikProps = {
    initialValues,
    onSubmit,
    enableReinitialize,
    validationSchema,
  };

  const formik = useDialogFormik(formikProps);

  const renderHeader = () => {
    const { title, subTitle, headerIconSize } = props;

    return (
      <DialogHeader
        title={title}
        subTitle={subTitle}
        Icon={Icon}
        headerIconSize={headerIconSize}
        hasChildren={hasChildren}
      />
    );
  };

  const renderChildren = () => {
    const { preLoadingHeight, contentClassName } = props;
    return (
      <DialogContent
        preLoading={preLoading}
        preLoadingHeight={preLoadingHeight}
        contentClassName={contentClassName}
      >
        {children}
      </DialogContent>
    );
  };

  const renderActions = () => {
    const {
      submitButtonName,
      closeButtonName,
      variant,
      disableSubmit,
      renderActionsFunc,
      ariaLabelledby,
    } = props;
    return (
      <DialogActions
        variant={variant}
        closeButtonName={closeButtonName}
        renderActionsFunc={renderActionsFunc}
        onClose={onClose}
        isLoading={isLoading}
        submitButtonName={submitButtonName}
        disableSubmit={disableSubmit}
        preLoading={preLoading}
        ariaLabelledby={ariaLabelledby}
      />
    );
  };

  const renderStepper = () => {
    if (!hasSteps) return;
    return (
      <StepperContainer>
        <Stepper steps={steps} activeStep={activeStep} orientation="vertical" />
      </StepperContainer>
    );
  };

  const { className, ariaLabelledby } = props;

  return (
    <Dialog
      onClose={onClose}
      className={className}
      formik={formik}
      ariaLabelledby={ariaLabelledby}
    >
      <Form formik={formik}>
        <FormContainer>
          {renderStepper()}
          <DialogContainer>
            {renderHeader()}
            {renderChildren()}
            {renderActions()}
          </DialogContainer>
        </FormContainer>
      </Form>
    </Dialog>
  );
}

const FormContainer = styled("div")(() => ({
  display: "flex",
  alignSelf: "stretch",
  flex: "1 0 0",
  background: "var(--surface-surface-15, #EFF5FC)",
  maxWidth: "100%",
}));

const StepperContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "row",
  alignSelf: "stretch",
  padding: "24px",
  alignItems: "flex-start",
}));

const DialogContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  flex: "1 0 0",
  alignSelf: "stretch",
  overflow: "hidden",
  borderRadius: "28px",
  background: "var(--surface-surface, #FCFDFF)",
}));

export const DialogFormPropTypes = {
  ...DialogPropTypes,
  ...DialogActionsPropTypes,
  ...DialogContentPropTypes,
  ...DialogHeaderPropTypes,
  initialValues: PropTypes.object,
  withCrossButton: PropTypes.bool,
  validationSchema: PropTypes.object,
  enableReinitialize: PropTypes.bool,
  ariaLabelledby: PropTypes.string,
  onSubmit: PropTypes.func,
  preLoading: PropTypes.bool,
};

DialogForm.propTypes = DialogFormPropTypes;

export default DialogForm;
